import cn from 'classnames';
import React, { FC, useRef, useState } from 'react';
import { HowManyUsersHaveCompletedWizardChart } from 'src/Components/Partials/Analytics/Charts/HowManyUsersHaveCompletedWizardChart';

import { Board } from '@.partials';
import { AgeChart, FragranceTypeChart, GenderChart, PrintoutCountsPerItemChart, StrengthChart, WhiffCountsPerItemChart } from '@/Components/Partials/Analytics';
import { BestCombinationIngredientsChart } from '@/Components/Partials/Analytics/Charts/BestCombinationIngredientsChart';
import { CustomersWizardStepsChart } from '@/Components/Partials/Analytics/Charts/CustomersWizardStepsChart';
import { FavoriteHeartChart } from '@/Components/Partials/Analytics/Charts/FavoriteHeartChart';
import { PreferableProductsChart } from '@/Components/Partials/Analytics/Charts/PreferableProductsChart';
import { Statistic } from '@/Store';

import S from './styles.module.scss';

type AnalyticsPageChartsProps = {
    statisticDataset: Statistic;
};

/**
 * TODO: I think in future charts will be separate by tariffs.
 */
export const AnalyticsPageCharts: FC<AnalyticsPageChartsProps> = ({ statisticDataset }) => {
    const chartRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [highlighted, setHighlighted] = useState<string | null>(null);

    const scrollToBigChart = (key: string) => {
        const el = chartRefs.current[key];
        if (!el) return;

        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlighted(key);
        setTimeout(() => setHighlighted(null), 3000);
    };

    return (
        <>
            <div className={S['mini-charts_wrapper']}>
                {statisticDataset.age && (
                    <Board.Item onClick={() => scrollToBigChart('age')} className={S['chart-mini']}>
                        <AgeChart data={statisticDataset.age.dataset} mini />
                    </Board.Item>
                )}
                {statisticDataset.favorite_heart && (
                    <Board.Item onClick={() => scrollToBigChart('favorite_heart')} className={S['chart-mini']}>
                        <FavoriteHeartChart
                            data={statisticDataset.favorite_heart.dataset}
                            bestProduct={statisticDataset.favorite_heart.best_product}
                            totalHearts={statisticDataset.favorite_heart.total_hearts}
                            totalProducts={statisticDataset.favorite_heart.total_products}
                            limit={statisticDataset.favorite_heart.limit}
                            mini
                        />
                    </Board.Item>
                )}
                {statisticDataset.strength && (
                    <Board.Item onClick={() => scrollToBigChart('strength')} className={S['chart-mini']}>
                        <StrengthChart data={statisticDataset.strength.dataset} mini />
                    </Board.Item>
                )}
                {statisticDataset.fragrance_type && (
                    <Board.Item onClick={() => scrollToBigChart('fragrance_type')} className={S['chart-mini']}>
                        <FragranceTypeChart data={statisticDataset.fragrance_type.dataset} mini />
                    </Board.Item>
                )}
                {statisticDataset.gender && (
                    <Board.Item onClick={() => scrollToBigChart('gender')} className={S['chart-mini']}>
                        <GenderChart data={statisticDataset.gender.dataset} mini />
                    </Board.Item>
                )}
                {statisticDataset.whiff_counts_per_item_brand && (
                    <Board.Item onClick={() => scrollToBigChart('whiff_counts_per_item_brand')} className={S['chart-mini']}>
                        <WhiffCountsPerItemChart
                            data={statisticDataset.whiff_counts_per_item_brand.dataset}
                            bestProduct={statisticDataset.whiff_counts_per_item_brand.best_product}
                            brands={statisticDataset.whiff_counts_per_item_brand.brands}
                            totalWhiffs={statisticDataset.whiff_counts_per_item_brand.total_whiffs}
                            mini
                        />
                    </Board.Item>
                )}
                {statisticDataset.printout_counts_per_item_brand && (
                    <Board.Item onClick={() => scrollToBigChart('printout_counts_per_item_brand')} className={S['chart-mini']}>
                        <PrintoutCountsPerItemChart
                            data={statisticDataset.printout_counts_per_item_brand.dataset}
                            bestProduct={statisticDataset.printout_counts_per_item_brand.best_product}
                            brands={statisticDataset.printout_counts_per_item_brand.brands}
                            totalPrintouts={statisticDataset.printout_counts_per_item_brand.total_printouts}
                            mini
                        />
                    </Board.Item>
                )}
                {statisticDataset.how_many_users_have_completed_wizard && (
                    <Board.Item onClick={() => scrollToBigChart('how_many_users_have_completed_wizard')} className={S['chart-mini']}>
                        <HowManyUsersHaveCompletedWizardChart data={statisticDataset.how_many_users_have_completed_wizard.dataset} mini />
                    </Board.Item>
                )}
                {statisticDataset.customers_and_wizard_steps && (
                    <Board.Item onClick={() => scrollToBigChart('customers_and_wizard_steps')} className={S['chart-mini']}>
                        <CustomersWizardStepsChart data={statisticDataset.customers_and_wizard_steps.dataset} mini />
                    </Board.Item>
                )}
                {statisticDataset.ingredients_best_combinations && (
                    <Board.Item onClick={() => scrollToBigChart('ingredients_best_combinations')} className={S['chart-mini']}>
                        <BestCombinationIngredientsChart data={statisticDataset.ingredients_best_combinations} mini />
                    </Board.Item>
                )}
                {statisticDataset.preferable_products && (
                    <Board.Item onClick={() => scrollToBigChart('preferable_products')} className={S['chart-mini']}>
                        <PreferableProductsChart data={statisticDataset.preferable_products} mini />
                    </Board.Item>
                )}
            </div>
            <div className={S.charts_wrapper}>
                {statisticDataset.age && (
                    <Board.Item ref={(el) => (chartRefs.current.age = el)} className={cn(S.chart, S['chart-large'], { [S['flash']]: highlighted === 'age' })}>
                        <AgeChart data={statisticDataset.age.dataset} />
                    </Board.Item>
                )}

                {statisticDataset.favorite_heart && (
                    <Board.Item
                        ref={(el) => (chartRefs.current.favorite_heart = el)}
                        className={cn(S.chart, S['chart-large'], { [S['flash']]: highlighted === 'favorite_heart' })}
                    >
                        <FavoriteHeartChart
                            data={statisticDataset.favorite_heart.dataset}
                            bestProduct={statisticDataset.favorite_heart.best_product}
                            totalHearts={statisticDataset.favorite_heart.total_hearts}
                            totalProducts={statisticDataset.favorite_heart.total_products}
                            limit={statisticDataset.favorite_heart.limit}
                        />
                    </Board.Item>
                )}

                {statisticDataset.strength && (
                    <Board.Item
                        ref={(el) => (chartRefs.current.strength = el)}
                        className={cn(S.chart, S['chart-small'], { [S['flash']]: highlighted === 'strength' })}
                    >
                        <StrengthChart data={statisticDataset.strength.dataset} />
                    </Board.Item>
                )}

                {statisticDataset.fragrance_type && (
                    <Board.Item
                        ref={(el) => (chartRefs.current.fragrance_type = el)}
                        className={cn(S.chart, S['chart-medium'], { [S['flash']]: highlighted === 'fragrance_type' })}
                    >
                        <FragranceTypeChart data={statisticDataset.fragrance_type.dataset} />
                    </Board.Item>
                )}

                {statisticDataset.gender && (
                    <Board.Item
                        ref={(el) => (chartRefs.current.gender = el)}
                        className={cn(S.chart, S['chart-small'], { [S['flash']]: highlighted === 'gender' })}
                    >
                        <GenderChart data={statisticDataset.gender.dataset} />
                    </Board.Item>
                )}

                {statisticDataset.how_many_users_have_completed_wizard && (
                    <Board.Item
                        ref={(el) => (chartRefs.current.how_many_users_have_completed_wizard = el)}
                        className={cn(S.chart, S['chart-small'], { [S['flash']]: highlighted === 'how_many_users_have_completed_wizard' })}
                    >
                        <HowManyUsersHaveCompletedWizardChart data={statisticDataset.how_many_users_have_completed_wizard.dataset} />
                    </Board.Item>
                )}

                {statisticDataset.whiff_counts_per_item_brand && (
                    <Board.Item
                        ref={(el) => (chartRefs.current.whiff_counts_per_item_brand = el)}
                        className={cn(S.chart, S['chart-large'], { [S['flash']]: highlighted === 'whiff_counts_per_item_brand' })}
                    >
                        <WhiffCountsPerItemChart
                            data={statisticDataset.whiff_counts_per_item_brand.dataset}
                            bestProduct={statisticDataset.whiff_counts_per_item_brand.best_product}
                            brands={statisticDataset.whiff_counts_per_item_brand.brands}
                            totalWhiffs={statisticDataset.whiff_counts_per_item_brand.total_whiffs}
                        />
                    </Board.Item>
                )}

                {statisticDataset.printout_counts_per_item_brand && (
                    <Board.Item
                        ref={(el) => (chartRefs.current.printout_counts_per_item_brand = el)}
                        className={cn(S.chart, S['chart-large'], { [S['flash']]: highlighted === 'printout_counts_per_item_brand' })}
                    >
                        <PrintoutCountsPerItemChart
                            data={statisticDataset.printout_counts_per_item_brand.dataset}
                            bestProduct={statisticDataset.printout_counts_per_item_brand.best_product}
                            brands={statisticDataset.printout_counts_per_item_brand.brands}
                            totalPrintouts={statisticDataset.printout_counts_per_item_brand.total_printouts}
                        />
                    </Board.Item>
                )}

                {statisticDataset.customers_and_wizard_steps && (
                    <Board.Item
                        ref={(el) => (chartRefs.current.customers_and_wizard_steps = el)}
                        className={cn(S.chart, S['chart-large'], { [S['flash']]: highlighted === 'customers_and_wizard_steps' })}
                    >
                        <CustomersWizardStepsChart data={statisticDataset.customers_and_wizard_steps.dataset} />
                    </Board.Item>
                )}

                {statisticDataset.ingredients_best_combinations && (
                    <Board.Item
                        ref={(el) => (chartRefs.current.ingredients_best_combinations = el)}
                        className={cn(S.chart, S['chart-half-large'], { [S['flash']]: highlighted === 'ingredients_best_combinations' })}
                    >
                        <BestCombinationIngredientsChart data={statisticDataset.ingredients_best_combinations} />
                    </Board.Item>
                )}
                {statisticDataset.preferable_products && (
                    <Board.Item
                        ref={(el) => (chartRefs.current.preferable_products = el)}
                        className={cn(S.chart, S['chart-half-large'], { [S['flash']]: highlighted === 'preferable_products' })}
                    >
                        <PreferableProductsChart data={statisticDataset.preferable_products} />
                    </Board.Item>
                )}
            </div>
        </>
    );
};
